import React, { useState, useEffect, useRef } from 'react';

interface ScrollIndicatorProps {
  className?: string;
}

const ScrollIndicator: React.FC<ScrollIndicatorProps> = ({ className = '' }) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  const updateScrollProgress = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = (scrollTop / docHeight) * 100;
    
    setScrollProgress(progress);
    
    // Show indicator if there's scrollable content
    setIsVisible(docHeight > 50);
  };

  const scrollToPosition = (percentage: number) => {
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const targetScrollTop = (percentage / 100) * docHeight;
    window.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth'
    });
  };

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!trackRef.current || isDragging) return;
    
    const rect = trackRef.current.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const clickPercentage = (clickY / rect.height) * 100;
    
    scrollToPosition(clickPercentage);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !trackRef.current) return;
    
    const rect = trackRef.current.getBoundingClientRect();
    const mouseY = e.clientY - rect.top;
    const percentage = Math.max(0, Math.min(100, (mouseY / rect.height) * 100));
    
    scrollToPosition(percentage);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging || !trackRef.current) return;
    
    e.preventDefault();
    const rect = trackRef.current.getBoundingClientRect();
    const touchY = e.touches[0].clientY - rect.top;
    const percentage = Math.max(0, Math.min(100, (touchY / rect.height) * 100));
    
    scrollToPosition(percentage);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    // Initial check
    updateScrollProgress();

    window.addEventListener('scroll', updateScrollProgress);
    window.addEventListener('resize', updateScrollProgress);

    return () => {
      window.removeEventListener('scroll', updateScrollProgress);
      window.removeEventListener('resize', updateScrollProgress);
    };
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging]);

  if (!isVisible) return null;

  return (
    <div className={`fixed right-4 top-1/2 transform -translate-y-1/2 z-40 ${className}`}>
      {/* Luxury Interactive Scroll Track */}
      <div 
        ref={trackRef}
        className="w-3 h-32 rounded-full backdrop-blur-sm cursor-pointer transition-all duration-200 relative group"
        style={{
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 50%, rgba(255, 255, 255, 0.1) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(255, 255, 255, 0.1)'
        }}
        onClick={handleTrackClick}
        title="Klik om te scrollen - Sleep om te navigeren"
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 50%, rgba(255, 255, 255, 0.15) 100%)';
          e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 50%, rgba(255, 255, 255, 0.1) 100%)';
          e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.1)';
        }}
      >
        {/* Luxury Progress Bar */}
        <div 
          ref={progressRef}
          className={`absolute left-0 w-full rounded-full transition-all duration-300 ease-out cursor-grab hover:cursor-grab ${
            isDragging ? 'cursor-grabbing' : ''
          }`}
          style={{ 
            height: `${Math.max(12, scrollProgress)}%`,
            minHeight: '12px',
            top: '0px',
            background: 'linear-gradient(180deg, #4F6660 0%, #FF8E53 30%, #FFB366 60%, #FFC599 85%, #FFD700 100%)',
            boxShadow: isDragging 
              ? '0 3px 10px rgba(255, 107, 53, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)' 
              : '0 2px 6px rgba(255, 107, 53, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)'
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {/* Subtle inner highlight */}
          <div 
            className="absolute inset-0 rounded-full"
            style={{
              background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.05) 50%, rgba(255, 255, 255, 0) 100%)'
            }}
          />
        </div>
        
        {/* Luxury Progress Handle */}
        <div 
          className={`absolute left-1/2 transform -translate-x-1/2 w-4 h-4 transition-all duration-300 cursor-grab hover:cursor-grab ${
            isDragging ? 'scale-125 cursor-grabbing' : 'scale-100'
          } opacity-0 group-hover:opacity-100`}
          style={{ 
            top: `${Math.max(8, scrollProgress - 2)}%`,
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, #4F6660 0%, #FF8E53 25%, #FFB366 50%, #FFC599 75%, #FFD700 100%)',
            borderRadius: '2px',
            boxShadow: isDragging 
              ? '0 4px 12px rgba(255, 107, 53, 0.4), 0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)' 
              : '0 2px 8px rgba(255, 107, 53, 0.3), 0 1px 2px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.15)'
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {/* Inner shine effect */}
          <div 
            className="absolute inset-0 rounded-sm"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.05) 100%)',
              borderRadius: '2px'
            }}
          />
          {/* Subtle border */}
          <div 
            className="absolute inset-0 rounded-sm"
            style={{
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '2px'
            }}
          />
        </div>
      </div>
      
      {/* Scroll Hint */}
      <div className="absolute -right-10 top-1/2 transform -translate-y-1/2 opacity-60 hover:opacity-100 transition-opacity duration-300">
        <div className="flex flex-col items-center space-y-1">
          <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce"></div>
          <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        </div>
      </div>

      {/* Scroll Percentage */}
      <div className="absolute -right-14 top-1/2 transform -translate-y-1/2 text-xs text-gray-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        {Math.round(scrollProgress)}%
      </div>

      {/* Instructions Tooltip */}
      <div className="absolute -right-32 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
        Klik om te springen<br />
        Sleep om te navigeren
      </div>
    </div>
  );
};

export default ScrollIndicator;
